<template>
    <div>
        <div v-if="copy_address_label" class="row p-t-10 p-b-0" v-cloak>
            <div class="col-lg-3">
                <label>
                    <input name="is_copy_address" type="checkbox" class="m-1-10" v-model="is_copy_address"
                        @change="copyAddress">
                    {{ copy_address_label }}
                </label>
            </div>
        </div>

        <div class="row p-b-10">
            <div class="col-sm-3">
                <div class="fg-line form-group">
                    <label :for="field_prefix + 'address1'">Address 1</label>
                    <input type="text" :id="field_prefix + 'address1'" class="form-control"
                        v-model="address.address1" />
                </div>
            </div>
            <div class="col-sm-2">
                <div class="fg-line form-group">
                    <label :for="field_prefix + 'address2'">Address 2</label>
                    <input type="text" :id="field_prefix + 'address2'" class="form-control" placeholder="optional"
                        v-model="address.address2" />
                </div>
            </div>
            <div class="col-sm-2">
                <div class="form-group fg-line">
                    <label :for="field_prefix + 'city'">City</label>
                    <input type="text" :id="field_prefix + 'city'" class="form-control" v-model="address.city" />
                </div>
            </div>
            <div class="col-sm-3">
                <div class="form-group fg-line">
                    <label :for="field_prefix + 'state_cd'">State/Province</label>
                    <select :id="field_prefix + 'state_cd'" class="form-control" v-model="address.state_cd"
                        :class="{ disabled: filteredStates && filteredStates.length > 0 }">
                        <option v-for="state in filteredStates" :value="state.state_cd" v-html="state.state_nm">
                        </option>
                    </select>
                </div>
            </div>
            <div class="col-sm-2">
                <div class="form-group">
                    <div class="fg-line form-group">
                        <label :for="field_prefix + 'postal_cd'" class="text-nowrap">Postal Code</label>
                        <input type="text" :id="field_prefix + 'postal_cd'" class="form-control"
                            v-model="address.postal_cd" />
                    </div>
                </div>
            </div>

        </div>

        <div class="row p-b-10" style="position: relative;">
            <div class="col-sm-3" v-if="show_county">
                <div class="fg-line form-group county-select" :class="{ 'has-error': countyInvalid }">
                    <label>County</label>
                    <infinite-multiselect v-if="address.country_cd === 'US'" v-model="address.county"
                        :options="countyOptions" :searchable="true" :show-labels="false" :allow-empty="true"
                        :disabled="!address.state_cd || county_loading" deselect-label="" select-label=""
                        :placeholder="countyPlaceholder" @close="county_touched = true">
                        <span slot="noResult">No county found.</span>
                        <span slot="noOptions">Select a state first.</span>
                    </infinite-multiselect>
                    <input v-else type="text" :id="field_prefix + 'county'" class="form-control" placeholder="optional"
                        v-model="address.county" @blur="county_touched = true" />
                    <span class="help-block" v-if="countyInvalid">
                        Please select a county.
                    </span>
                </div>
            </div>
            <div class="col-sm-3">
                <div class="form-group fg-line">
                    <label :for="field_prefix + 'country_cd'">Country</label>
                    <select :id="field_prefix + 'country_cd'" class="form-control" v-model=address.country_cd>
                        <option v-for="country in filteredCountries" :value="country.country_cd"
                            v-html="country.country_nm">
                        </option>
                    </select>
                </div>
            </div>
            <div class="col-sm-5 form-group m-t-25" v-if="show_residential">
                <label class="cursor-pointer">
                    <input v-model="address.is_residential" type="checkbox"> Address is Residential
                </label>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@import '../../../sass/global_variables';

input[name="is_residential"] {
    margin-right: 5px;
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
.county-select {
    .multiselect {
        min-height: 30px;
        font-size: inherit;
        color: #555;
    }

    .multiselect__tags {
        min-height: 30px;
        height: 30px;
        padding: 5px 30px 5px 5px;
        border: 1px solid rgb(175, 175, 175);
        border-radius: 2px;
        font-size: inherit;
        line-height: 18px;
    }

    .multiselect__select {
        height: 28px;
        padding: 6px 8px;

        &:before {
            border-color: #999 transparent transparent;
            top: 60%;
        }
    }

    .multiselect__single,
    .multiselect__placeholder,
    .multiselect__input {
        margin-bottom: 0;
        padding: 0;
        font-size: inherit;
        line-height: 18px;
    }

    .multiselect__placeholder {
        color: #999;
    }

    .multiselect--disabled {
        background: transparent;

        .multiselect__tags {
            background-color: #eee;
        }

        .multiselect__single {
            background: transparent;
        }
    }

    .multiselect__content-wrapper {
        border: 1px solid rgb(175, 175, 175);
        border-radius: 2px;
    }

    .multiselect--active .multiselect__tags {
        border-color: rgb(130, 130, 130);
    }

    &.has-error .multiselect__tags {
        border-color: #a94442;
    }
}
</style>

<script>
import { fetchCountiesForState, isCountyValid, clearCountyIfInvalid } from './sanitizeCounty';

export default {
    props: {
        usa_only: { default: false },
        field_prefix: { default: '' },
        copy_address: { default: null },
        copy_address_label: '',
        initial_address: {},
        show_residential: { type: Boolean, default: true },
        show_county: { type: Boolean, default: true }
    },

    data: function () {
        return {
            address: {
                address1: '',
                address2: '',
                city: '',
                state_cd: '',
                postal_cd: '',
                county: '',
                country_cd: 'US',
                is_residential: false,
            },
            countries: [{ country_cd: '', country_nm: 'Select Country' }],
            states: [{ state_cd: '', state_nm: 'Select State' }],
            addressControl: undefined,
            is_copy_address: false,
            addressY: {},
            result: {},
            is_loading: true,
            county_touched: false,
            countyOptions: [],
            countyOptionsCache: {},
            county_loading: false,
        }
    },

    watch: {

        initial_address: {
            deep: true,
            handler() {
                let vm = this;
                if (this.is_loading) {
                    this.is_loading = false;
                    this.getCountries();
                    this.getStates(function () {
                        var nextAddr = Object.assign({}, vm.initial_address);

                        if (!vm.initial_address.country_cd) {
                            nextAddr.country_cd = 'US';
                            nextAddr.state_cd = '';
                        }

                        vm.address = vm.normalizeAddress(nextAddr);
                    });
                }
            }
        },

        filteredStates: {
            handler() {
                if (this.filteredStates.length === 2) {
                    this.address.state_cd = this.filteredStates[1].state_cd;
                }
            }
        },

        show_residential: function (val) {
            if (!val) {
                this.address.is_residential = false;
            }
        },

        show_county: function (val) {
            if (!val) {
                this.address.county = '';
            }
        },

        'address.country_cd': function (newCountry, oldCountry) {
            if (newCountry === oldCountry) {
                return;
            }
            if (this.filteredStates.length !== 2) {
                this.address.state_cd = '';
            }
            this.address.county = '';
        },

        'address.state_cd': function (newState, oldState) {
            if (newState === oldState) {
                return;
            }
            this.fetchCountyOptions(newState);
        },

        address: {
            handler() {
                this.normalizeAddress(this.address);
                this.$emit('update', this.address);
            },
            deep: true
        }
    },
    computed: {
        filteredStates() {
            let vm = this;
            if (this.states) {
                return this.states.filter(function (state) {
                    return (state.country_cd === vm.address.country_cd || !state.country_cd)
                })
            } else {
                return null;
            }
        },
        filteredCountries() {
            if (this.usa_only) {
                this.address.country_cd = 'US';
                return this.countries.filter(function (item) {
                    return item.country_cd === 'US';
                })
            } else {
                return this.countries;
            }
        },
        countyInvalid() {
            return this.show_county
                && this.county_touched
                && this.address.country_cd === 'US'
                && !isCountyValid(this.address);
        },
        countyPlaceholder() {
            if (!this.address.state_cd) return 'Select a state first';
            if (this.county_loading) return 'Loading counties...';
            return 'Select county';
        },
    },

    methods: {
        normalizeAddress: function (addr) {
            if (!addr) return addr;

            if (!this.show_residential) {
                addr.is_residential = false;
            }

            if (!this.show_county) {
                addr.county = '';
            }

            return addr;
        },

        copyAddress() {
            if (this.is_copy_address) {
                var nextAddr = Object.assign({}, this.copy_address);
                this.address = this.normalizeAddress(nextAddr);
            }
        },

        updateAddress() {
            this.address = this.normalizeAddress(this.address);
            this.$emit('update', this.address);
        },

        initializeAddressy() {
            let keyEl = document.getElementById('addressy-key');
            if (!keyEl) return;

            let key = keyEl.getAttribute('content');
            if (!key) return;

            let searchCountries = "US,AU,BM,BO,BR,BZ,CA,DE,FR,GB,GT,GU,IE,IN,JP,LK,MX,NL,NO,NZ,PK,PR,SE,TT,UAE,UK,ZA";
            let config = { key: key, bar: { visible: false }, search: { countries: searchCountries } };

            let fields = [
                { element: this.field_prefix + 'address1', field: 'Line1', mode: pca.fieldMode.SEARCH },
                { element: this.field_prefix + 'country_cd', field: 'CountryIso2', mode: pca.fieldMode.COUNTRY }
            ];

            this.addressControl = new pca.Address(fields, config);
            this.addressControl.listen('populate', this.selectedSearchedAddress);
        },

        selectedSearchedAddress(address, variations) {
            this.addressY = address;

            var nextAddr = {
                address1: address.Line1,
                address2: address.Line2,
                city: address.City,
                state_cd: address.ProvinceCode,
                county: address.AdminAreaName || '',
                country_cd: address.CountryIso2,
                postal_cd: address.PostalCode,
                is_residential: (address.Type === 'Residential' ? true : false)
            };

            this.address = this.normalizeAddress(nextAddr);
        },

        getCountries(success) {
            if (this.countries.length === 1) {
                let request = this.$http.get('/country-list');
                request.then(function (response) {
                    this.countries.splice(1, this.countries.length);
                    if (response.data) {
                        for (let i = 0; i < response.data.length; i++) {
                            this.countries.push(response.data[i]);
                        }
                    }
                    if (typeof success === 'function') success(response);
                });
            }
        },

        getStates(success) {

            if (this.usa_only && this.states.length === 1) {
                let request = this.$http.get('/states-list', { params: { country_cd: 'US' } });
                request.then(function (response) {

                    this.states.splice(1, this.states.length);

                    if (response.data) {
                        for (let i = 0; i < response.data.length; i++) {
                            this.states.push(response.data[i]);
                        }
                    }
                    if (typeof success === 'function') success(response);
                });
            } else if (this.states.length === 1) {
                let request = this.$http.get('/all-states');
                request.then(function (response) {
                    this.states.splice(1, this.states.length);

                    if (response.data) {
                        for (let i = 0; i < response.data.length; i++) {
                            this.states.push(response.data[i]);
                        }
                    }
                    if (typeof success === 'function') success(response);
                });
            }
        },

        fetchCountyOptions(stateCd) {
            let vm = this;
            let state = (stateCd || '').toUpperCase();

            let applyList = function (list) {
                vm.countyOptions = list;
                clearCountyIfInvalid(vm.address, list);
            };

            if (!state) {
                applyList([]);
                return;
            }

            if (this.countyOptionsCache[state]) {
                applyList(this.countyOptionsCache[state]);
                return;
            }

            this.county_loading = true;
            fetchCountiesForState(this.$http, state).then(function (list) {
                vm.countyOptionsCache[state] = list;
                if ((vm.address.state_cd || '').toUpperCase() === state) {
                    applyList(list);
                }
                vm.county_loading = false;
            }).catch(function () {
                vm.county_loading = false;
            });
        },
    },

    mounted: function () {

        this.initializeAddressy();
        let vm = this;
        if (this.countries.length === 1) {
            this.getCountries(function () {
                vm.getStates();
            });
        }
    }
}

</script>



// WEBPACK FOOTER //
// address-entry.vue?6e35f5bf